import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { CartProvider, useCart } from "./context/CartContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { Toaster } from "./components/ui/sonner";
import Header from "./components/Header";
import Footer from "./components/Footer";
import PwaRelocationBanner from "./components/PwaRelocationBanner";
import CheckoutDrawer from "./components/CheckoutDrawer";
import { captureAttribution } from "./lib/attribution";
import { trackPageView } from "./lib/gtag";
import GlobalStickyCart from "./components/GlobalStickyCart";
import StickyMobileCTA from "./components/StickyMobileCTA";
import ScrollToTop from "./components/ScrollToTop";
import CookieBanner from "./components/shared/CookieBanner";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import InstallDeepLink from "./components/InstallDeepLink";
import React, { lazy, Suspense, useEffect } from "react";

// Core Pages — nur die Startseite bleibt im Hauptbundle, alle weiteren Seiten laden lazy (Code-Splitting)
const LandingPage = lazy(() => import("./pages/LandingPage"));
const InsuranceLandingPage = lazy(() => import("./pages/InsuranceLandingPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const TransactionsPage = lazy(() => import("./pages/TransactionsPage"));
const VehiclesPage = lazy(() => import("./pages/VehiclesPage"));
const FuelXCardPage = lazy(() => import("./pages/FuelXCardPage"));
const SubscriptionsPage = lazy(() => import("./pages/SubscriptionsPage"));
const HotelsPage = lazy(() => import("./pages/HotelsPage"));
const HotelSearchPage = lazy(() => import("./pages/HotelSearchPage"));
const HotelResultsPage = lazy(() => import("./pages/HotelResultsPage"));
const HotelDetailPage = lazy(() => import("./pages/HotelDetailPage"));
const GasStationsPage = lazy(() => import("./pages/GasStationsPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const SubscriptionSuccessPage = lazy(() => import("./pages/SubscriptionSuccessPage"));
const WarenkorbPage = lazy(() => import("./pages/WarenkorbPage"));
const CardBestellenPage = lazy(() => import("./pages/CardBestellenPage"));

// Lazy-loaded Landing Pages
const FuelXCardLandingPage = lazy(() => import("./pages/FuelXCardLandingPage"));
const HowItWorksPage = lazy(() => import("./pages/HowItWorksPage"));
const BusinessPage = lazy(() => import("./pages/BusinessPage"));
const BusinessLandingPage = lazy(() => import("./pages/BusinessLandingPage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const ComparisonPage = lazy(() => import("./pages/ComparisonPage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const NetworkPage = lazy(() => import("./pages/NetworkPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const CareersPage = lazy(() => import("./pages/CareersPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const SavingsPage = lazy(() => import("./pages/SavingsPage"));
const SilverCardPage = lazy(() => import("./pages/SilverCardPage"));
const GoldCardPage = lazy(() => import("./pages/GoldCardPage"));
const LuxushotelsPage = lazy(() => import("./pages/LuxushotelsPage"));
const SellCarPage = lazy(() => import("./pages/SellCarPage"));
const TuevServicePage = lazy(() => import("./pages/TuevServicePage"));
const KfzSteuerPage = lazy(() => import("./pages/KfzSteuerPage"));
const OelServicePage = lazy(() => import("./pages/OelServicePage"));
const ReifenWechselPage = lazy(() => import("./pages/ReifenWechselPage"));
const AutokreditPage = lazy(() => import("./pages/AutokreditPage"));
const PannenhilfePage = lazy(() => import("./pages/PannenhilfePage"));
const FleetInsurancePage = lazy(() => import("./pages/FleetInsurancePage"));
const BusinessVorteilePage = lazy(() => import("./pages/BusinessVorteilePage"));
const GeschaeftswagenPage = lazy(() => import("./pages/GeschaeftswagenPage"));
const BewertenPage = lazy(() => import("./pages/BewertenPage"));
const CheckoutPage = lazy(() => import("./pages/CheckoutPage"));
const CheckoutSuccessPage = lazy(() => import("./pages/CheckoutSuccessPage"));
const MagicLinkPage = lazy(() => import("./pages/MagicLinkPage"));
const USPLandingPage = lazy(() => import("./pages/USPLandingPage"));
const KilometerLandingPage = lazy(() => import("./pages/KilometerLandingPage"));
const BusinessFuhrparkPage = lazy(() => import("./pages/BusinessFuhrparkPage"));
const BusinessFlottenvorteilePage = lazy(() => import("./pages/BusinessFlottenvorteilePage"));
const BusinessGeschaeftsreisenLandingPage = lazy(() => import("./pages/BusinessGeschaeftsreisenLandingPage"));
const BusinessMitarbeiterBenefitPage = lazy(() => import("./pages/BusinessMitarbeiterBenefitPage"));
const BusinessRechnerPage = lazy(() => import("./pages/BusinessRechnerPage"));
const BusinessLeasingServicePage = lazy(() => import("./pages/BusinessLeasingServicePage"));
const VorteilePage = lazy(() => import("./pages/VorteilePage"));
const RatgeberWarnleuchte = lazy(() => import("./pages/ratgeber/WarnleuchtePage"));
const RatgeberAutokosten = lazy(() => import("./pages/ratgeber/AutokostenSenkenPage"));
const RatgeberWerkstattkosten = lazy(() => import("./pages/ratgeber/WerkstattkostenPruefenPage"));
const TankstellenIndexPage = lazy(() => import("./pages/tankstellen/TankstellenIndexPage"));
const TankstellenCityPage = lazy(() => import("./pages/tankstellen/TankstellenCityPage"));
const TankstellenNaehePage = lazy(() => import("./pages/tankstellen/TankstellenNaehePage"));
const TankstellenkartePage = lazy(() => import("./pages/tankstellen/TankstellenkartePage"));
const RouteTankstellenPage = lazy(() => import("./pages/tankstellen/RouteTankstellenPage"));
const SpritpreisePage = lazy(() => import("./pages/spritpreise/SpritpreisePage"));
const BenzinpreisePage = lazy(() => import("./pages/spritpreise/BenzinpreisePage"));
const DieselpreisePage = lazy(() => import("./pages/spritpreise/DieselpreisePage"));
const E10PreisPage = lazy(() => import("./pages/spritpreise/E10PreisPage"));
const GuenstigTankenPage = lazy(() => import("./pages/spritpreise/GuenstigTankenPage"));
const Tankstelle24hPage = lazy(() => import("./pages/spritpreise/Tankstelle24hPage"));
const SpritpreiseCityPage = lazy(() => import("./pages/spritpreise/SpritpreiseCityPage"));
const DieselpreiseCityPage = lazy(() => import("./pages/spritpreise/DieselpreiseCityPage"));
const MarkenIndexPage = lazy(() => import("./pages/spritpreise/MarkenIndexPage"));
const MarkenBrandPage = lazy(() => import("./pages/spritpreise/MarkenBrandPage"));
const TankkartePage = lazy(() => import("./pages/tankkarte/TankkartePage"));
const TankkartePrivatPage = lazy(() => import("./pages/tankkarte/TankkartePrivatPage"));
const TankkarteFirmenPage = lazy(() => import("./pages/tankkarte/TankkarteFirmenPage"));
const TankrabattPage = lazy(() => import("./pages/tankkarte/TankrabattPage"));
const TankguthabenPage = lazy(() => import("./pages/tankkarte/TankguthabenPage"));
const SpritkostenRechnerPage = lazy(() => import("./pages/rechner/SpritkostenRechnerPage"));
const SpritverbrauchsRechnerPage = lazy(() => import("./pages/rechner/SpritverbrauchsRechnerPage"));
const PendlerkostenRechnerPage = lazy(() => import("./pages/rechner/PendlerkostenRechnerPage"));
const AutokostenPage = lazy(() => import("./pages/rechner/AutokostenPage"));
const ReichweitenRechnerPage = lazy(() => import("./pages/rechner/ReichweitenRechnerPage"));
const RatgeberIndexPage = lazy(() => import("./pages/ratgeber/RatgeberIndexPage"));
const RatgeberWannTanken = lazy(() => import("./pages/ratgeber/WannTankenPage"));
const RatgeberAutobahn = lazy(() => import("./pages/ratgeber/AutobahnTankenPage"));
const RatgeberSpritverbrauchSenken = lazy(() => import("./pages/ratgeber/SpritverbrauchSenkenPage"));
const RatgeberLohntTankkarte = lazy(() => import("./pages/ratgeber/LohntSichTankkartePage"));
const RatgeberAlternative = lazy(() => import("./pages/ratgeber/AlternativeKraftstoffePage"));
const SorgenRatgeberPage = lazy(() => import("./pages/ratgeber/SorgenRatgeberPage"));
// Werkstatt-SEO-Plattform: leichte Routenliste (voller Content wird lazy geladen)
import WERKSTATT_ROUTE_PATHS from "./data/werkstatt/paths.json";
const WerkstattHubPage = lazy(() => import("./pages/werkstatt/WerkstattHubPage"));
const WerkstattSeoPage = lazy(() => import("./pages/werkstatt/WerkstattSeoPage"));
const WERKSTATT_PATH_SET = new Set(WERKSTATT_ROUTE_PATHS);

/* Public pages with Header + Footer */
const PUBLIC_PAGES = new Set([
  '/', '/fuelx-card', '/versicherung', '/so-funktionierts', '/fuer-unternehmen',
  '/preise', '/vergleich', '/faq', '/tankstellen-netzwerk', '/ueber-uns',
  '/karriere', '/kontakt', '/sparen', '/silver-card', '/gold-card',
  '/vorteile',
  '/corporate', '/zusatzkarte', '/empfehlen', '/upgrade',
  '/business-zusatzkarten', '/luxushotels', '/geschaeftsreisen',
  '/hotel-partner', '/business/branchen', '/business/reporting', '/presse',
  '/business/vorteile', '/business/geschaeftswagen', '/bewerten',
  '/hotel-suche',
  '/auto-verkaufen',
  '/tuev-service',
  '/kfz-steuer',
  '/oel-service',
  '/reifen-wechsel',
  '/autokredit',
  '/pannenhilfe',
  '/flottenversicherung',
  '/fuelx-hotels',
  '/tankstellen', '/tankstellen-in-meiner-naehe',
  '/spritpreise', '/benzinpreise', '/dieselpreise', '/e10-preis', '/guenstig-tanken',
  '/24h-tankstelle', '/tankkarte', '/tankkarte-privat', '/tankkarte-firmen', '/tankrabatt',
  '/spritkostenrechner', '/spritverbrauchsrechner', '/ratgeber',
  '/datenschutz', '/agb', '/impressum', '/widerruf', '/vertrag-widerrufen', '/barrierefreiheit', '/cookies',
  '/zahlung-und-lieferung',
]);

function LazyExtraPage({ component }) {
  const Comp = lazy(() => import("./pages/ExtraPages").then(m => ({ default: m[component] })));
  return <Suspense fallback={<div className="min-h-screen pt-16" />}><Comp /></Suspense>;
}

function SimplePage({ title, children }) {
  return (
    <div className="min-h-screen bg-white pt-16">
      <section className="py-24 sm:py-32">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <h1 className="text-5xl sm:text-6xl font-medium tracking-tight text-stone-900 mb-8">{title}</h1>
          {children}
        </div>
      </section>
    </div>
  );
}

function AppLayout() {
  const location = useLocation();
  // UTM-/Klick-Parameter (Google Ads, Meta) bei jedem Seitenaufruf erfassen — für Kauf-Attribution
  useEffect(() => { captureAttribution(); }, [location.search]);
  // Meta PageView (Browser+CAPI dedupliziert) + GA4 page_view bei jedem Routenwechsel
  useEffect(() => { trackPageView(location.pathname); }, [location.pathname]);
  // SEO-Shell bleibt im DOM (LCP-Kandidat), aber für Screenreader ausblenden sobald die App steht
  useEffect(() => { document.getElementById('seo-shell')?.setAttribute('aria-hidden', 'true'); }, []);
  const showLayout =
    PUBLIC_PAGES.has(location.pathname) ||
    location.pathname.startsWith('/vorteile/') ||
    location.pathname.startsWith('/ratgeber/') ||
    location.pathname.startsWith('/tankstellen/') ||
    location.pathname.startsWith('/spritpreise/') ||
    location.pathname.startsWith('/dieselpreise/') ||
    location.pathname.startsWith('/business/') ||
    location.pathname === '/werkstatt' ||
    location.pathname.startsWith('/werkstatt/') ||
    WERKSTATT_PATH_SET.has(location.pathname.replace(/\/+$/, ''));

  return (
    <>
      <ScrollToTop />
      <PwaRelocationBanner />
      {showLayout && <Header />}
      <main id="main">
      <Suspense fallback={<div className="min-h-screen pt-16" />}>
        <Routes>
          {/* Core Landing */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/kilometer-rechner" element={<KilometerLandingPage />} />
          <Route path="/km" element={<Navigate to="/kilometer-rechner" replace />} />
          <Route path="/reise" element={<Navigate to="/hotel-suche" replace />} />
          <Route path="/versicherung" element={<InsuranceLandingPage />} />

          {/* FuelX Card Pages */}
          <Route path="/silver-card" element={<SilverCardPage />} />
          <Route path="/gold-card" element={<GoldCardPage />} />
          <Route path="/preise" element={<PricingPage />} />
          <Route path="/vergleich" element={<ComparisonPage />} />
          <Route path="/so-funktionierts" element={<HowItWorksPage />} />
          <Route path="/sparen" element={<SavingsPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/fuer-unternehmen" element={<Navigate to="/business" replace />} />
          <Route path="/tankstellen-netzwerk" element={<NetworkPage />} />

          {/* Travel sub-pages */}
          <Route path="/luxushotels" element={<LuxushotelsPage />} />
          <Route path="/auto-verkaufen" element={<SellCarPage />} />
          <Route path="/tuev-service" element={<TuevServicePage />} />
          <Route path="/kfz-steuer" element={<KfzSteuerPage />} />
          <Route path="/werkstatt-reparatur" element={<Navigate to="/silver-card" replace />} />
          <Route path="/oel-service" element={<OelServicePage />} />
          <Route path="/reifen-wechsel" element={<ReifenWechselPage />} />
          <Route path="/autokredit" element={<AutokreditPage />} />
          <Route path="/pannenhilfe" element={<PannenhilfePage />} />
          <Route path="/flottenversicherung" element={<FleetInsurancePage />} />
          <Route path="/fuelx-hotels" element={<HotelSearchPage fuelx />} />
          <Route path="/fuelx-card-vorteil" element={<Navigate to="/fuelx-hotels" replace />} />
          <Route path="/bewerten" element={<BewertenPage />} />
          <Route path="/aufbereitung" element={<Navigate to="/silver-card" replace />} />
          <Route path="/geschaeftsreisen" element={<LazyExtraPage component="GeschaeftsreisenPage" />} />

          {/* USP-Landing-Pages (21 dynamische Vorteil-Seiten) */}
          <Route path="/vorteile/:slug" element={<USPLandingPage />} />
          <Route path="/vorteile" element={<VorteilePage />} />

          {/* Ratgeber (SEO-Landingpages mit echtem Nutzen) */}
          <Route path="/ratgeber/warnleuchte-am-auto" element={<RatgeberWarnleuchte />} />
          <Route path="/ratgeber/autokosten-senken" element={<RatgeberAutokosten />} />
          <Route path="/ratgeber/werkstattkosten-pruefen" element={<RatgeberWerkstattkosten />} />

          {/* Werkstatt-SEO-Plattform: zentraler Hub + 50 Landingpages (Daten: data/werkstatt) */}
          <Route path="/werkstatt" element={<WerkstattHubPage />} />
          {WERKSTATT_ROUTE_PATHS.map((p) => (
            <Route key={p} path={p} element={<WerkstattSeoPage />} />
          ))}

          <Route path="/business/vorteile/:slug" element={<USPLandingPage />} />
          <Route path="/business/vorteile" element={<Navigate to="/business/fuhrpark" replace />} />

          {/* Insurance sub-pages → alles auf die eine Versicherungs-Seite umgeleitet */}
          <Route path="/versicherung/*" element={<Navigate to="/versicherung" replace />} />

          {/* Business sub-pages */}
          <Route path="/business" element={<BusinessLandingPage />} />
          <Route path="/business/fuhrpark" element={<BusinessFuhrparkPage />} />
          <Route path="/business/flottenvorteile" element={<BusinessFlottenvorteilePage />} />
          <Route path="/business/geschaeftsreisen" element={<BusinessGeschaeftsreisenLandingPage />} />
          <Route path="/business/mitarbeiter-benefit" element={<BusinessMitarbeiterBenefitPage />} />
          <Route path="/business/rechner" element={<BusinessRechnerPage />} />
          <Route path="/business/leasing-service" element={<BusinessLeasingServicePage />} />
          <Route path="/business/branchen" element={<Navigate to="/business" replace />} />
          <Route path="/business/reporting" element={<Navigate to="/business" replace />} />
          <Route path="/business/geschaeftswagen" element={<Navigate to="/business/flottenvorteile" replace />} />

          {/* Trust & Info */}
          <Route path="/hotel-partner" element={<LazyExtraPage component="HotelPartnerPage" />} />
          <Route path="/presse" element={<LazyExtraPage component="PressePage" />} />

          {/* Legal */}
          <Route path="/datenschutz" element={<LazyExtraPage component="DatenschutzPage" />} />
          <Route path="/agb" element={<LazyExtraPage component="AGBPage" />} />
          <Route path="/impressum" element={<LazyExtraPage component="ImpressumPage" />} />
          <Route path="/widerruf" element={<LazyExtraPage component="WiderrufPage" />} />
          <Route path="/vertrag-widerrufen" element={<LazyExtraPage component="VertragWiderrufenPage" />} />
          <Route path="/zahlung-und-lieferung" element={<LazyExtraPage component="ZahlungLieferungPage" />} />
          <Route path="/barrierefreiheit" element={<LazyExtraPage component="BarrierefreiheitPage" />} />
          <Route path="/cookies" element={<LazyExtraPage component="CookiesPage" />} />

          {/* Extra pages */}
          <Route path="/empfehlen" element={<LazyExtraPage component="EmpfehlenPage" />} />
          <Route path="/corporate" element={<LazyExtraPage component="CorporateCardPage" />} />
          <Route path="/zusatzkarte" element={<LazyExtraPage component="ZusatzkartePage" />} />
          <Route path="/upgrade" element={<LazyExtraPage component="UpgradePage" />} />
          <Route path="/business-zusatzkarten" element={<LazyExtraPage component="BusinessZusatzkartePage" />} />

          {/* Company */}
          <Route path="/ueber-uns" element={<AboutPage />} />
          <Route path="/karriere" element={<CareersPage />} />
          <Route path="/kontakt" element={<ContactPage />} />

          {/* Hotel Engine */}
          <Route path="/hotel-suche" element={<HotelSearchPage />} />
          <Route path="/hotel-suche/ergebnisse" element={<HotelResultsPage />} />
          <Route path="/hotel-suche/hotel/:hotelId" element={<HotelDetailPage />} />
          <Route path="/tankstellen" element={<TankstellenIndexPage />} />
          <Route path="/tankstellen/marken" element={<MarkenIndexPage />} />
          <Route path="/tankstellen/marken/:brand" element={<MarkenBrandPage />} />
          <Route path="/tankstellen/:slug" element={<TankstellenCityPage />} />
          <Route path="/tankstellen-in-meiner-naehe" element={<TankstellenNaehePage />} />
          <Route path="/tankstellenkarte" element={<TankstellenkartePage />} />
          <Route path="/tankstellen-auf-der-route" element={<RouteTankstellenPage />} />
          <Route path="/spritpreise-europa" element={<GasStationsPage />} />
          {/* Keyword-Cluster: Spritpreise, Rechner & Tankkarten */}
          <Route path="/spritpreise" element={<SpritpreisePage />} />
          <Route path="/spritpreise/:slug" element={<SpritpreiseCityPage />} />
          <Route path="/benzinpreise" element={<BenzinpreisePage />} />
          <Route path="/dieselpreise" element={<DieselpreisePage />} />
          <Route path="/dieselpreise/:slug" element={<DieselpreiseCityPage />} />
          <Route path="/e10-preis" element={<E10PreisPage />} />
          <Route path="/guenstig-tanken" element={<GuenstigTankenPage />} />
          <Route path="/24h-tankstelle" element={<Tankstelle24hPage />} />
          <Route path="/tankkarte" element={<TankkartePage />} />
          <Route path="/tankkarte-privat" element={<TankkartePrivatPage />} />
          <Route path="/tankkarte-firmen" element={<TankkarteFirmenPage />} />
          <Route path="/tankrabatt" element={<TankrabattPage />} />
          <Route path="/tankguthaben" element={<TankguthabenPage />} />
          <Route path="/spritkostenrechner" element={<SpritkostenRechnerPage />} />
          <Route path="/spritverbrauchsrechner" element={<SpritverbrauchsRechnerPage />} />
          <Route path="/pendlerkosten-rechner" element={<PendlerkostenRechnerPage />} />
          <Route path="/autokosten" element={<AutokostenPage />} />
          <Route path="/reichweiten-rechner" element={<ReichweitenRechnerPage />} />
          <Route path="/ratgeber" element={<RatgeberIndexPage />} />
          <Route path="/ratgeber/wann-tanken-am-guenstigsten" element={<RatgeberWannTanken />} />
          <Route path="/ratgeber/guenstig-tanken-autobahn" element={<RatgeberAutobahn />} />
          <Route path="/ratgeber/alternative-kraftstoffe" element={<RatgeberAlternative />} />
          <Route path="/ratgeber/spritverbrauch-senken" element={<RatgeberSpritverbrauchSenken />} />
          <Route path="/ratgeber/lohnt-sich-eine-tankkarte" element={<RatgeberLohntTankkarte />} />
          <Route path="/ratgeber/auto-macht-probleme" element={<SorgenRatgeberPage slug="auto-macht-probleme" />} />
          <Route path="/ratgeber/werkstatt-abzocke-vermeiden" element={<SorgenRatgeberPage slug="werkstatt-abzocke-vermeiden" />} />
          <Route path="/ratgeber/kfz-termine-nie-verpassen" element={<SorgenRatgeberPage slug="kfz-termine-nie-verpassen" />} />
          <Route path="/ratgeber/kfz-versicherung-zu-teuer" element={<SorgenRatgeberPage slug="kfz-versicherung-zu-teuer" />} />
          <Route path="/ratgeber/laufende-autokosten-im-griff" element={<SorgenRatgeberPage slug="laufende-autokosten-im-griff" />} />
          <Route path="/ratgeber/panne-unfall-was-tun" element={<SorgenRatgeberPage slug="panne-unfall-was-tun" />} />
          <Route path="/ratgeber/autokauf-verkauf-ohne-verlust" element={<SorgenRatgeberPage slug="autokauf-verkauf-ohne-verlust" />} />
          <Route path="/ratgeber/kleine-schaeden-guenstig-reparieren" element={<SorgenRatgeberPage slug="kleine-schaeden-guenstig-reparieren" />} />
          <Route path="/ratgeber/auto-urlaubscheck" element={<SorgenRatgeberPage slug="auto-urlaubscheck" />} />
          <Route path="/ratgeber/auto-sicherheit-zuverlaessigkeit" element={<SorgenRatgeberPage slug="auto-sicherheit-zuverlaessigkeit" />} />
          <Route path="/subscription-success" element={<SubscriptionSuccessPage />} />
          <Route path="/warenkorb" element={<WarenkorbPage />} />
          <Route path="/card-bestellen" element={<CardBestellenPage />} />
          <Route path="/cart" element={<WarenkorbPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
          <Route path="/auth/magic" element={<MagicLinkPage />} />

          {/* Auth */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* Protected */}
          <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/transactions" element={<ProtectedRoute><TransactionsPage /></ProtectedRoute>} />
          <Route path="/vehicles" element={<ProtectedRoute><VehiclesPage /></ProtectedRoute>} />
          <Route path="/fuelx-card" element={<FuelXCardLandingPage />} />
          <Route path="/dashboard/card" element={<ProtectedRoute><FuelXCardPage /></ProtectedRoute>} />
          <Route path="/subscriptions" element={<ProtectedRoute><SubscriptionsPage /></ProtectedRoute>} />
          <Route path="/hotels" element={<ProtectedRoute><HotelsPage /></ProtectedRoute>} />
          <Route path="/bookings" element={<ProtectedRoute><HotelsPage /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute adminOnly><AdminPage /></ProtectedRoute>} />
          <Route path="/admin/widerrufe" element={<ProtectedRoute adminOnly><AdminPage /></ProtectedRoute>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      </main>
      {showLayout && <Footer />}
      {showLayout && <GlobalStickyCart />}
      {showLayout && <StickyMobileCTA />}
      <GlobalCheckoutDrawer />
    </>
  );
}

function GlobalCheckoutDrawer() {
  const { isOpen, closeCart, config, setConfig } = useCart();
  return (
    <CheckoutDrawer
      open={isOpen}
      onClose={closeCart}
      config={config}
      onConfigChange={setConfig}
    />
  );
}

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <BrowserRouter>
          <AppLayout />
          <CookieBanner />
          <PWAInstallPrompt />
          <InstallDeepLink />
        </BrowserRouter>
        <Toaster position="top-right" />
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
